import React, { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { PLANS, usePaywall } from "@/lib/access";
import { useAuth } from "@/lib/auth";

function IntervalToggle({ value, onChange }) {
  return (
    <div className="inline-flex border-2 border-[#111]" role="tablist" data-testid="plan-interval-toggle">
      {["monthly", "yearly"].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`plan-interval-${v}`}
          aria-selected={value === v}
          className={`px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase font-extrabold transition-colors ${value === v ? "bg-[#111] text-[#F4F3EC]" : "text-[#111] hover:bg-[#e8e7df]"}`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function PlanCard({ plan, interval, prefix }) {
  const { user } = useAuth();
  const { startCheckout, pending } = usePaywall();
  const isCurrent = user?.plan === plan.key;
  const busy = pending === `${plan.key}_${interval}`;
  const yearly = interval === "yearly";
  return (
    <div data-testid={`${prefix}-tier-${plan.key}`} className="relative border-2 border-[#111] bg-[#F4F3EC] text-[#111] p-6 flex flex-col">
      <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#111] text-[#F4F3EC] font-mono text-[10px] tracking-[0.25em] uppercase font-extrabold">
        {plan.useCase}
      </div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold text-[#555] mt-1">{plan.name}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-5xl font-extrabold tracking-tight" data-testid={`${prefix}-price-${plan.key}`}>
          {yearly ? plan.priceYearly : plan.priceMonthly}
        </span>
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase font-bold text-[#555]">/{yearly ? "year" : "month"}</span>
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap font-mono text-xs tracking-[0.12em] uppercase font-bold text-[#555]">
        {yearly ? <span className="px-2 py-0.5 border-2 border-[#111] text-[#111]">{plan.yearlySavings}</span> : <span>or {plan.priceYearly}/year</span>}
      </div>
      <p className="mt-4 text-sm text-[#333]">{plan.tagline}</p>
      <ul className="mt-5 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed">
            <Check size={14} strokeWidth={2.8} className="mt-1 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        disabled={isCurrent || !!pending}
        onClick={() => startCheckout(plan.key, interval)}
        data-testid={`${prefix}-cta-${plan.key}`}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 border-2 border-[#111] bg-[#111] text-[#F4F3EC] px-4 py-3 font-display font-extrabold uppercase tracking-[0.12em] text-sm hover:bg-[#F4F3EC] hover:text-[#111] transition-colors focus-ring active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy && <Loader2 size={14} className="spin-slow" />}
        {isCurrent ? "Current plan" : `Subscribe to ${plan.name}`}
      </button>
    </div>
  );
}

export default function PlanCards({ prefix = "pricing" }) {
  const [interval, setInterval] = useState("monthly");
  return (
    <div data-testid={`${prefix}-plan-cards`}>
      <div className="mb-6"><IntervalToggle value={interval} onChange={setInterval} /></div>
      <div className="grid md:grid-cols-3 gap-6">
        {PLANS.map((p) => <PlanCard key={p.key} plan={p} interval={interval} prefix={prefix} />)}
      </div>
      <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#777] font-bold">
        Secure checkout by Stripe · Card & Apple Pay · Cancel anytime
      </p>
    </div>
  );
}
